import UserRolesTableField from 'account/components/userManagement/tenants/UserRolesTableField'
import { listUsers } from 'account/components/userManagement/users/new-actions'
import { usersSelector } from 'account/components/userManagement/users/selectors'
import { getDomainId } from 'api-client/helpers'
import { listResmgrHosts } from 'app/plugins/openstack/components/infrastructure/actions'
import { resmgrHostsSelector } from 'app/plugins/openstack/components/infrastructure/selectors'
import { setDefaultVolumeTypeTenant } from 'app/plugins/openstack/components/storage/volume-types/actions'
import VolumeTypesPicklist from 'app/plugins/openstack/components/storage/volume-types/VolumeTypesPicklist'
import { getValidVolumeTypes } from 'app/plugins/openstack/components/storage/volumes/helpers'
import Progress from 'core/components/progress/Progress'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import { isSystemAdminUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import DomainNameTextField from '../domains/DomainNameTextField'
import DomainPickList from '../domains/DomainPickList'
import { createTenant, listUserTenants } from './new-actions'

interface Props {
  addRoute: Route
  reload: (force?: boolean, reset?: boolean) => void
  domainId: string
}

export default function CreateTenantModal({ addRoute, reload, domainId }: Props) {
  const { history } = useReactRouter()
  const [loadingVolumeBackends, setLoadingVolumeBackends] = useState(true)
  const [activeVolumeBackends, setActiveVolumeBackends] = useState([])
  const defaultParams = {
    name: '',
    description: '',
    volumeType: '',
    roleAssignments: {},
    domainId: domainId,
  }
  const { reload: reloadTenants } = useListAction(listUserTenants)
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const { update, updating, error, reset } = useUpdateAction(createTenant)
  const {
    update: setDefaultFn,
    updating: updatingTenantVolumeType,
    error: tenantVolumeTypeError,
    reset: resetTenantVolumeType,
  } = useUpdateAction(setDefaultVolumeTypeTenant)
  const { loading, reload: reloadUsers } = useListAction(listUsers, {
    params: {
      domainId: params.domainId,
    },
  })
  const users = useSelector(usersSelector)

  const { loading: loadingHost } = useListAction(listResmgrHosts)
  const hosts = useSelector(resmgrHostsSelector)

  useEffect(() => {
    getValidVolumeTypes({ hosts, setActiveVolumeBackends, setLoadingVolumeBackends })
  }, [hosts, setActiveVolumeBackends, setLoadingVolumeBackends])

  useEffect(() => {
    reloadUsers(true)
  }, [params?.domainId])

  useEffect(() => {
    setParams({ ...defaultParams, domainId })
  }, [domainId])

  const submitForm = useCallback(async () => {
    const body = {
      name: params.name,
      description: params.description,
      roleAssignments: params.roleAssignments,
      domain_id: params.domainId,
    }
    const { success, response } = await update(body)
    if (success) {
      if (params.volumeType) {
        const { success: volumeTypeSuccess } = await setDefaultFn({
          id: response?.id,
          body: { volume_type: params.volumeType },
        })
        if (volumeTypeSuccess) {
          resetTenantVolumeType()
        } else {
          return
        }
      }
      // reload tenants if the created tenant is in the same domain
      if (getDomainId() === params.domainId) {
        reloadTenants(true, true)
      }
      reload(true, true)
      handleClose()
    }
  }, [params])

  const volumeTypeFilterFn = useCallback(
    (options, volumeTypes) =>
      options.filter((option) => {
        const backendName = volumeTypes.find((volume) => volume.name === option.value)?.extra_specs
          ?.volume_backend_name
        return (
          activeVolumeBackends.includes(backendName) &&
          option?.label &&
          option?.label !== '__DEFAULT__'
        )
      }),
    [activeVolumeBackends],
  )

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    history.push(routeManager.routes.userManagement.tenants.path())
  }

  return (
    <ModalForm
      route={addRoute}
      title={`Create Tenant`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating || updatingTenantVolumeType}
      error={error || tenantVolumeTypeError}
      initialValues={defaultParams}
      loading={loadingHost || loadingVolumeBackends}
      submitTitle={`Create Tenant`}
    >
      <>
        <FormFieldSection title="Tenant Settings">
          <TextField
            id="name"
            label="Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            required
          />
          <TextField
            id="description"
            label="Description"
            onChange={getParamsUpdater('description')}
            value={params.description}
          />
          {isSystemAdminUser() ? (
            <DomainPickList onChange={getParamsUpdater('domainId')} value={params.domainId} />
          ) : (
            <DomainNameTextField domainId={params.domainId} isReadOnly />
          )}
          <PicklistField
            DropdownComponent={VolumeTypesPicklist}
            id="volumeType"
            onChange={getParamsUpdater('volumeType')}
            value={params.volumeType}
            label="Default Volume Type"
            filterFn={volumeTypeFilterFn}
            required
          />
        </FormFieldSection>
        <FormFieldSection title="User Assignment">
          <Progress renderContentOnMount={!loading} loading={loading} message={'Loading Users...'}>
            <UserRolesTableField
              // validations={userRolesValidations}
              id="roleAssignments"
              users={users}
              onChange={getParamsUpdater('roleAssignments')}
            />
          </Progress>
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
